import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import type { Balance } from '../api/types';
import { Amount } from './format';

export type BalanceKind = 'agents' | 'purchasers' | 'suppliers' | 'banks' | 'currencies';

const LABEL: Record<BalanceKind, string> = {
  agents: 'Outstanding balance',
  purchasers: 'Seller balance',
  suppliers: 'Dispatcher balance',
  banks: 'Bank balance',
  currencies: 'Cash balance',
};

/**
 * `currencyId` matters only for `kind: 'purchasers'` and `kind: 'suppliers'` (Sellers and Dispatchers
 * are both multi-currency: one id can have several balance rows, one per currency) - other kinds
 * ignore it. Omitting it for a multi-currency party lets the API fall back to its own default rather
 * than silently picking the wrong currency.
 */
export function useBalance(kind: BalanceKind, id: number | string | undefined | null, currencyId?: number | string | undefined | null) {
  const n = id === undefined || id === null || id === '' ? undefined : Number(id);
  const c = currencyId === undefined || currencyId === null || currencyId === '' ? undefined : Number(currencyId);
  return useQuery({
    queryKey: ['balance', kind, n, c],
    queryFn: () => api.get<Balance>(`/balances/${kind}/${n}`, c ? { currencyId: c } : undefined),
    enabled: !!n,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    staleTime: 10_000,
  });
}

/** Every live row's current balance at once, e.g. for a master list's own "Current balance" column. */
export function useBalances(kind: BalanceKind) {
  return useQuery({
    queryKey: ['balance', kind, 'all'],
    queryFn: () => api.get<Balance[]>(`/balances/${kind}`),
    staleTime: 10_000,
  });
}

/**
 * Current balance of the selected party, shown beside transaction forms.
 * Renders nothing when the user is not allowed to see balances (403) or nothing is selected.
 */
export function BalanceCard({ kind, id, currencyId, title, decimals }: { kind: BalanceKind; id: number | string | undefined | null; currencyId?: number | string | undefined | null; title?: string; decimals?: number }) {
  const q = useBalance(kind, id, currencyId);
  if (!id || q.isError) return null;
  return (
    <div className="mms-balance rounded border p-2 px-3 mb-3 d-flex justify-content-between align-items-center" aria-live="polite">
      <span className="text-body-secondary small">{title ?? LABEL[kind]}</span>
      {q.isPending ? (
        <span className="placeholder-glow"><span className="placeholder col-12" style={{ width: 90 }} /></span>
      ) : (
        <strong className="fs-6"><Amount value={q.data.balance} code={q.data.currencyCode} decimals={decimals} /></strong>
      )}
    </div>
  );
}
